/**
 * Shared primitive schemas reused across the entity DTOs.
 */
import { z } from "zod";

/**
 * An opaque, server-generated entity primary key (or foreign key `*Id`).
 *
 * Historically these fields were declared `z.string().uuid()` on the
 * assumption that every row carries a Prisma `@default(uuid())` id. That
 * holds for real (Clerk-created) rows, but the demo / seed tenants mint
 * READABLE, non-UUID ids on purpose — e.g.
 * `seed-demob-project-planned-demo-pennine-biz`, `seed-demob-worker-1-…`,
 * `seed-demob-doc-…`. Those are perfectly valid primary keys; the strict
 * `.uuid()` check simply didn't recognise them.
 *
 * Because the mobile list/detail routes run `Schema.parse(serializedResponse)`
 * at runtime, a `.uuid()` mismatch threw a `ZodError` that the route's
 * try/catch turned into an HTTP 500 — but ONLY for the demo accounts whose
 * ids aren't UUID-shaped. Real users were unaffected.
 *
 * Entity ids are opaque on the wire: the mobile client never parses their
 * internal structure, and tenancy / existence are enforced server-side by
 * ownership-scoped queries, never by the id's textual format. So we validate
 * only "non-empty string" here and let uuid / cuid / seed ids all pass.
 *
 * Use this for every server-generated `id` and `*Id` field. Do NOT use it for
 * client-supplied format guards that are unrelated to entity ids.
 */
export const entityIdSchema = z.string().min(1);

/**
 * Ceiling for a client-supplied sort position (`orderIndex`).
 *
 * Every `orderIndex` column in the schema is a Prisma `Int` — a PostgreSQL
 * `int4`, max 2,147,483,647 — and every append computes `max + 1`. So a
 * validator looser than the column does two things, both bad: a value above
 * `int4` turns a client error into a 500 (the driver throws where a 400
 * belonged), and a value AT the column's ceiling is accepted, stored, and then
 * permanently breaks appends for that parent row, since `max + 1` overflows on
 * every subsequent create (#2334 round 7).
 *
 * A million is far above any per-parent row cap in this product (plots are
 * capped at `PLOT_PROJECT_MAX` = 500) and leaves ~2,000× headroom below `int4`,
 * so `max + 1` can never reach the column's limit.
 */
export const ORDER_INDEX_MAX = 1_000_000;

/**
 * A client-supplied sort position, bounded by what the column can hold.
 *
 * Shared rather than repeated: the bound belongs to the COLUMN TYPE, so every
 * `orderIndex` on every entity wants the same one, and a per-call-site literal
 * is a bound that drifts. Server-assigned positions are always ≥ 0.
 */
export const orderIndexSchema = z.number().int().min(0).max(ORDER_INDEX_MAX);

/**
 * The `int4` column range, for schemas that MIRROR a Prisma `Int` column's
 * stored value (output DTOs). A read-side schema must accept every value the
 * column can hold — a product-sane cap there would refuse to parse a legacy
 * row — so DTO mirrors use the column's own limits (precedent: the diary
 * weather fields, which were the first to carry these).
 */
export const INT32_MAX = 2_147_483_647;
export const INT32_MIN = -2_147_483_648;

/**
 * Ceiling for a client-supplied count that lands in a Prisma `Int` column
 * (`contractorCount`, `durationMinutes`, `readingMinutes`, …).
 *
 * Same failure class as `ORDER_INDEX_MAX`, minus the append-brick: a validator
 * looser than the column turns garbage input into a 500 (or persists `1.5`
 * where the driver then chokes), so every count wants `.int()` plus an upper
 * bound. 100,000 is ~10× the peak workforce of the largest construction
 * project in UK history, so no legitimate value is refused, while the bound
 * stays ~20,000× below `int4`.
 */
export const COUNT_MAX = 100_000;

/**
 * A client-supplied count, bounded by what its `Int` column can hold.
 *
 * Shared for the same reason as `orderIndexSchema`: the bound belongs to the
 * column type, and a per-call-site literal is a bound that drifts.
 */
export const countSchema = z.number().int().min(0).max(COUNT_MAX);

/**
 * The project's contractor count as a document generation request carries it.
 *
 * A Project may store 0 ("Estimated workers on site" accepts it), but no
 * document prints a workforce of zero, so 0 means "not set" here rather than
 * a rejection that blocks every document for that project.
 */
export const generationContractorCountSchema = countSchema
  .nullable()
  .optional()
  .transform((count) => (count === 0 ? null : count));
